/** Sprint 5 · Form Builder · 表單製作器（左欄位列表 + 右屬性 panel + 底部 lifecycle）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Paper,
  Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import { getForm, updateForm, submitReview, FieldSpec, FieldType } from './services/formBuilderService';

const FUNC = 'form_manager';
const TYPES: FieldType[] = ['text', 'textarea', 'number', 'date', 'datetime',
  'select', 'radio', 'checkbox', 'file', 'hidden'];
const HAS_OPTIONS = (t: FieldType) => t === 'select' || t === 'radio' || t === 'checkbox';

const newField = (n: number): FieldSpec => ({
  order: n, label: { 'zh-TW': `欄位${n + 1}` }, field_name: `field_${n + 1}`,
  field_type: 'text', field_kind: 'data', required: false, length: null,
});

const FormBuilderPage: React.FC = () => {
  const { id } = useParams();
  const fid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const canUpdate = hasPermission(FUNC, 'update');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('draft');
  const [rejection, setRejection] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldSpec[]>([]);
  const [sel, setSel] = useState(0);

  const load = useCallback(async () => {
    try {
      const f = await getForm(fid);
      setTitle(f.title); setDescription(f.description || ''); setStatus(f.status);
      setRejection(f.rejection_reason || null); setFields(f.fields || []);
    } catch (err) { showApiError(err); }
  }, [fid, showApiError]);

  useEffect(() => { if (!permLoading && hasPermission(FUNC, 'read')) load(); }, [permLoading, hasPermission, load]);

  const isDraft = status === 'draft';
  const patch = (i: number, p: Partial<FieldSpec>) =>
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...p } : f)));
  const move = (i: number, dir: -1 | 1) => setFields((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const c = [...prev]; [c[i], c[j]] = [c[j], c[i]];
    return c.map((f, idx) => ({ ...f, order: idx }));
  });
  const addField = () => { setFields((p) => [...p, newField(p.length)]); setSel(fields.length); };
  const removeField = (i: number) => { setFields((p) => p.filter((_, idx) => idx !== i)); setSel(0); };

  const save = async (): Promise<boolean> => {
    try {
      await updateForm(fid, { title, description, fields });
      showSuccess('SYS420001'); return true;
    } catch (err) { showApiError(err); return false; }
  };
  const doSubmit = async () => {
    if (!(await save())) return;
    try { const f = await submitReview(fid); setStatus(f.status); showSuccess('SYS420001'); }
    catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!hasPermission(FUNC, 'read')) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限')}</Box>;

  const cur = fields[sel];
  const os = cur?.options_source || null;
  const setOS = (p: Record<string, unknown> | null) => patch(sel, { options_source: p as never });

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" onClick={() => nav('/form_manager')}>← {tt('back', '返回列表')}</Button>
        <Typography variant="h6">{tt('builderTitle', '表單製作器')}</Typography>
        <Typography variant="caption" color="text.secondary">（{tt(`st_${status}`, status)}）</Typography>
      </Stack>
      {rejection && <Alert severity="warning" sx={{ mb: 1 }}>{tt('rejected', '退回原因')}：{rejection}</Alert>}
      {!isDraft && <Alert severity="info" sx={{ mb: 1 }}>{tt('notDraftHint', '非草稿狀態不可編輯欄位。')}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField size="small" fullWidth label={tt('title', '表單標題')} value={title}
          disabled={!isDraft} onChange={(e) => setTitle(e.target.value)} />
        <TextField size="small" fullWidth label={tt('desc', '說明')} value={description}
          disabled={!isDraft} onChange={(e) => setDescription(e.target.value)} />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* 左：欄位列表 */}
        <Paper variant="outlined" sx={{ width: 300, p: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{tt('fields', '欄位')}（{fields.length}）</Typography>
            {isDraft && <Button size="small" startIcon={<AddIcon />} onClick={addField}>{tt('add', '新增')}</Button>}
          </Stack>
          {fields.map((f, i) => (
            <Box key={i} onClick={() => setSel(i)} sx={{
              p: 0.75, mb: 0.5, borderRadius: 1, cursor: 'pointer',
              border: '1px solid', borderColor: i === sel ? 'primary.main' : 'divider',
              bgcolor: i === sel ? 'action.hover' : 'transparent',
            }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {(f.label?.['zh-TW']) || f.field_name}
                  <Typography component="span" variant="caption" color="text.secondary"> · {f.field_type}</Typography>
                </Typography>
                {isDraft && <>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeField(i); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                </>}
              </Stack>
            </Box>
          ))}
        </Paper>

        {/* 右：屬性 panel */}
        <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
          {!cur ? <Typography variant="body2" color="text.secondary">{tt('pickField', '選一個欄位以設定屬性')}</Typography> : (
            <Stack spacing={1.25}>
              <Typography variant="subtitle2">{tt('fieldProps', '欄位屬性')}</Typography>
              <TextField size="small" label={tt('label', '顯示名稱')} value={cur.label?.['zh-TW'] || ''}
                disabled={!isDraft} onChange={(e) => patch(sel, { label: { ...cur.label, 'zh-TW': e.target.value } })} />
              <TextField size="small" label={tt('fieldName', '欄位代碼 field_name')} value={cur.field_name}
                disabled={!isDraft} onChange={(e) => patch(sel, { field_name: e.target.value })} />
              <Stack direction="row" spacing={1}>
                <TextField size="small" select fullWidth label={tt('type', '表達方式')} value={cur.field_type}
                  disabled={!isDraft} onChange={(e) => patch(sel, { field_type: e.target.value as FieldType })}>
                  {TYPES.map((ty) => <MenuItem key={ty} value={ty}>{ty}</MenuItem>)}
                </TextField>
                <TextField size="small" select fullWidth label={tt('kind', '種類')} value={cur.field_kind || 'data'}
                  disabled={!isDraft} onChange={(e) => patch(sel, { field_kind: e.target.value as 'data' | 'computed' })}>
                  <MenuItem value="data">data</MenuItem>
                  <MenuItem value="computed">computed（{tt('reserved', '預留')}）</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" type="number" label={tt('length', '長度上限')} value={cur.length ?? ''}
                  disabled={!isDraft} onChange={(e) => patch(sel, { length: e.target.value === '' ? null : Number(e.target.value) })} />
                <FormControlLabel control={<Checkbox checked={cur.required} disabled={!isDraft}
                  onChange={(e) => patch(sel, { required: e.target.checked })} />} label={tt('required', '必填')} />
              </Stack>

              {HAS_OPTIONS(cur.field_type) && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">{tt('optionsSource', '選項值來源')}</Typography>
                  <TextField size="small" select label={tt('srcType', '來源')} value={os?.type || 'static'}
                    disabled={!isDraft}
                    onChange={(e) => setOS({ type: e.target.value })}>
                    <MenuItem value="static">{tt('src_static', '固定選項')}</MenuItem>
                    <MenuItem value="table">{tt('src_table', '資料表查找')}</MenuItem>
                    <MenuItem value="query">{tt('src_query', '自訂查詢 SQL')}</MenuItem>
                  </TextField>
                  {(!os || os.type === 'static') && (
                    <TextField size="small" multiline minRows={2} disabled={!isDraft}
                      label={tt('staticVals', '選項（每行一個）')}
                      value={(os?.values || []).join('\n')}
                      onChange={(e) => setOS({ type: 'static', values: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} />
                  )}
                  {os?.type === 'table' && (
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="table" value={os.table || ''} disabled={!isDraft}
                        onChange={(e) => setOS({ ...os, table: e.target.value })} />
                      <TextField size="small" label="display" value={os.display || ''} disabled={!isDraft}
                        onChange={(e) => setOS({ ...os, display: e.target.value })} />
                      <TextField size="small" label="value" value={os.value || ''} disabled={!isDraft}
                        onChange={(e) => setOS({ ...os, value: e.target.value })} />
                    </Stack>
                  )}
                  {os?.type === 'query' && (
                    <TextField size="small" multiline minRows={2} disabled={!isDraft}
                      label={tt('querySql', 'SQL（須含 :org_id 綁定）')}
                      helperText={tt('queryHint', '安全限制：只允許 SELECT、且必含 :org_id')}
                      value={os.sql || ''} onChange={(e) => setOS({ ...os, sql: e.target.value })} />
                  )}
                </>
              )}
            </Stack>
          )}
        </Paper>
      </Stack>

      {isDraft && canUpdate && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={save}>{tt('saveDraft', '儲存草稿')}</Button>
          <Button variant="outlined" color="primary" onClick={doSubmit}>{tt('submitReview', '送審')}</Button>
        </Stack>
      )}
    </Box>
  );
};

export default FormBuilderPage;
